import {
  addMinutes,
  addMonths,
  addYears,
  getDate,
  getDay,
  getMonth,
  isAfter,
  lastDayOfMonth,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { RRule, Weekday } from 'rrule';
import { Task } from '../../../utils/state/model/interfaces/objects';
import { DateTime } from 'luxon';
import { getNthFromDate } from './calendarUtils';
import { CustomValues } from '../components/CustomRecurrenceSelect';

const recurrenceCache_old: Map<string, Date> = new Map();

export const calculateCurrentDueDate_old = (task: Pick<Task, 'nonRecurringDueDate' | 'recurrence'>): Date | null => {
  if (task.nonRecurringDueDate) return task.nonRecurringDueDate;
  if (!task.recurrence) return null;

  const cachedDueDate = recurrenceCache_old.get(task.recurrence);
  if (cachedDueDate && cachedDueDate > new Date()) {
    return cachedDueDate;
  }

  const nextDueDate: Date = getNextRecurringDueDateAfter_old(task.recurrence, new Date());
  recurrenceCache_old.set(task.recurrence, nextDueDate);
  return nextDueDate;
};

const getNextRecurringDueDateAfter_old = (recurrence: string, after: Date): Date => {
  const rrule: RRule = RRule.fromString(recurrence);

  // This is a special case for custom monthly recurrence
  const monthlyCustom = calculateCustomMontlyRecurrence(rrule, after);
  if (monthlyCustom) return monthlyCustom;

  // If not a custom monthly recurrence, proceed with the normal calculation
  const nextRecurringDueDate = implementUtcDateToLocal(rrule.after(implementLocalDateToUtc(after))!);
  return nextRecurringDueDate;
};

export const implementLocalDateToUtc = (date: Date) => {
  const timeZoneOffset = date.getTimezoneOffset();
  const utcDate = addMinutes(date, -timeZoneOffset);
  return utcDate;
};

export const implementUtcDateToLocal = (date: Date) => {
  const localDate = DateTime.fromJSDate(date).toUTC().setZone('local', { keepLocalTime: true }).toJSDate();
  return localDate;
};

export const rruleByWeekdayOptions = new Map([
  [0, RRule.SU],
  [1, RRule.MO],
  [2, RRule.TU],
  [3, RRule.WE],
  [4, RRule.TH],
  [5, RRule.FR],
  [6, RRule.SA],
]);

export const getByWeekdayValue = (date: Date) => {
  const day = getDay(date);
  const weekdayNth = getNthFromDate(date);
  return rruleByWeekdayOptions.get(day)?.nth(weekdayNth);
};

export const createRecurrenceRule = (
  ruleOption: RRule,
  option: string,
  date: Date,
  tzid: string = DateTime.local().zoneName!
) => {
  const byweekdayValue = option === 'monthly-weekday' ? getByWeekdayValue(date) : null;
  const newRule = new RRule({
    ...ruleOption.origOptions,
    dtstart: implementLocalDateToUtc(date),
    byweekday: byweekdayValue || ruleOption.origOptions.byweekday,
    tzid: tzid,
  });

  return newRule;
};

export const createCustomRecurrenceRule = (
  rule: RRule | undefined,
  intervalValue: number,
  customValues: CustomValues | undefined
) => {
  if (!rule) return;

  const newRule = new RRule({
    ...rule.origOptions,
    interval: intervalValue,
  });
  // Generate weekly rule
  if (newRule.origOptions.freq === RRule.WEEKLY) {
    if (Array.isArray(customValues) && customValues.length === 0)
      return new RRule({
        ...newRule.origOptions,
        byweekday: undefined,
      });

    return new RRule({
      ...newRule.origOptions,
      byweekday: customValues as Weekday[],
    });
  }
  // Generate monthly rule
  if (newRule.origOptions.freq === RRule.MONTHLY) {
    if (!Array.isArray(customValues)) return;

    if (Array.isArray(customValues) && customValues.length === 0)
      return new RRule({
        ...newRule.origOptions,
        byweekday: undefined,
      });
    if (typeof customValues[0] === 'number') {
      return new RRule({
        ...newRule.origOptions,
        bymonthday: customValues as number[],
      });
    } else {
      return new RRule({
        ...newRule.origOptions,
        byweekday: customValues as Weekday[],
      });
    }
  }
  // Generate yearly rule
  if (newRule.origOptions.freq === RRule.YEARLY) {
    if (typeof customValues === 'string') {
      return new RRule({
        ...newRule.origOptions,
        bymonthday: getDate(parseISO(customValues)),
        bymonth: getMonth(parseISO(customValues)) + 1,
      });
    }
  }

  return newRule;
};

// Every month has these days, so a rule asking only for them can never overflow
const LAST_ALWAYS_VALID_DAY_OF_MONTH = 28;

// How far ahead we expand a recurrence when looking for concrete dates
const EXPANSION_WINDOW_IN_YEARS = 5;

/**
 * rrule follows RFC 5545 and simply skips an occurrence whose day-of-month does not exist,
 * so "the 31st, every month" yields nothing in November. We want it to fall on the last day
 * of the month instead, which means expanding those rules ourselves.
 *
 * Only MONTHLY rules asking for a day that can overflow need this. Everything else must be
 * left to rrule -- in particular YEARLY rules, which rrule fills in with a bymonthday derived
 * from dtstart. Treating those as monthly drops their bymonth constraint and collapses the
 * rule into a monthly one.
 */
export const needsMonthDayClamping = (rule: RRule): boolean =>
  rule.options.freq === RRule.MONTHLY && rule.options.bymonthday.some((day) => day > LAST_ALWAYS_VALID_DAY_OF_MONTH);

/**
 * Expands a monthly rule into concrete dates, clamping each requested day to the last valid
 * day of its month. Returns every occurrence from the rule's own dtstart through `until`,
 * sorted ascending; callers decide which of those they care about.
 *
 * Months are stepped from dtstart so the cycle keeps the phase the user picked -- anchoring
 * on today instead would shift "every 3 months from August" onto July/October/January.
 *
 * Only freq, interval, bymonthday and dtstart are honoured. That covers every monthly rule this
 * app can build today, but it means `count`, `until` and `bysetpos` are silently ignored, so a
 * recurrence with an end date would expand past its end. Add support here before offering one.
 */
export const expandClampedMonthlyDates = (rule: RRule, until: Date): Date[] => {
  // dtstart is stored shifted into UTC (see implementLocalDateToUtc), so shift it back to
  // recover the wall-clock date and time the user actually chose
  const start = implementUtcDateToLocal(rule.options.dtstart);
  const anchor = startOfMonth(start);
  const dates: Date[] = [];

  for (let step = 0; ; step++) {
    // Always step from the anchor. Stepping off the previous month lets a clamped day
    // (Feb 28) permanently shrink every month that follows it.
    const month = addMonths(anchor, step * rule.options.interval);
    if (isAfter(month, until)) break;

    const lastValidDay = lastDayOfMonth(month).getDate();
    for (const day of rule.options.bymonthday) {
      dates.push(
        new Date(
          month.getFullYear(),
          month.getMonth(),
          Math.min(day, lastValidDay),
          start.getHours(),
          start.getMinutes(),
          start.getSeconds()
        )
      );
    }
  }

  return dates.sort((a, b) => a.getTime() - b.getTime());
};

// This is a special case for custom monthly recurrence
const calculateCustomMontlyRecurrence = (rule: RRule, after: Date): Date | undefined => {
  if (!needsMonthDayClamping(rule)) return undefined;

  const dates = expandClampedMonthlyDates(rule, addYears(after, EXPANSION_WINDOW_IN_YEARS));
  return dates.find((date) => isAfter(date, after));
};
